import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  AdminPaymentSort,
  PaymentMethod,
  PaymentProvider,
  PaymentStatus,
} from '@/lib/api-admin/types';

export const PAYMENT_SORTABLE_COLUMNS = new Set(['createdAt', 'capturedAt', 'amount']);

export const SORT_COLUMN_TO_KEY: Record<string, { asc: AdminPaymentSort; desc: AdminPaymentSort }> = {
  createdAt: { asc: 'CREATED_AT_ASC', desc: 'CREATED_AT_DESC' },
  capturedAt: { asc: 'CREATED_AT_ASC', desc: 'CAPTURED_AT_DESC' },
  amount: { asc: 'AMOUNT_ASC', desc: 'AMOUNT_DESC' },
};

export const SORT_KEY_TO_COLUMN: Record<AdminPaymentSort, { id: string; dir: 'asc' | 'desc' }> = {
  CREATED_AT_DESC: { id: 'createdAt', dir: 'desc' },
  CREATED_AT_ASC: { id: 'createdAt', dir: 'asc' },
  CAPTURED_AT_DESC: { id: 'capturedAt', dir: 'desc' },
  AMOUNT_DESC: { id: 'amount', dir: 'desc' },
  AMOUNT_ASC: { id: 'amount', dir: 'asc' },
};

export const togglePaymentSort = (
  current: AdminPaymentSort,
  columnId: string,
): AdminPaymentSort => {
  const mapping = SORT_COLUMN_TO_KEY[columnId];
  if (!mapping) return current;
  const active = SORT_KEY_TO_COLUMN[current];
  if (active.id !== columnId) return mapping.desc;
  return active.dir === 'desc' ? mapping.asc : mapping.desc;
};

/** 결제 목록 PG사 필터 값 — 목록 응답 provider 표기와 같다(MANUAL = 수기 결제). */
export type PaymentProviderFilter = PaymentProvider | 'MANUAL';

export interface PaymentFiltersState {
  keyword: string;
  paymentStatus: PaymentStatus | null;
  paymentMethod: PaymentMethod | null;
  /** PG사(복수 선택, OR). 빈 배열 = 전체. payment #14 — TOSS / KAKAO / MANUAL. */
  provider: ReadonlyArray<PaymentProviderFilter>;
  fromDate: string | null;
  toDate: string | null;
  minAmount: number | null;
  maxAmount: number | null;
  hasRefund: boolean | null;
  /** true = 고도몰 이관 결제(sourcePgProvider=LEGACY) 제외. null = 포함(기본). */
  excludeLegacy: boolean | null;
  page: number;
  size: number;
  sort: AdminPaymentSort;
  setKeyword: (k: string) => void;
  setPaymentStatus: (s: PaymentStatus | null) => void;
  setPaymentMethod: (m: PaymentMethod | null) => void;
  setProvider: (provider: ReadonlyArray<PaymentProviderFilter>) => void;
  setFromDate: (d: string | null) => void;
  setToDate: (d: string | null) => void;
  setMinAmount: (n: number | null) => void;
  setMaxAmount: (n: number | null) => void;
  setHasRefund: (b: boolean | null) => void;
  setExcludeLegacy: (b: boolean | null) => void;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  setSort: (sort: AdminPaymentSort) => void;
  reset: () => void;
}

const INITIAL: Omit<
  PaymentFiltersState,
  | 'setKeyword'
  | 'setPaymentStatus'
  | 'setPaymentMethod'
  | 'setProvider'
  | 'setFromDate'
  | 'setToDate'
  | 'setMinAmount'
  | 'setMaxAmount'
  | 'setHasRefund'
  | 'setExcludeLegacy'
  | 'setPage'
  | 'setSize'
  | 'setSort'
  | 'reset'
> = {
  keyword: '',
  paymentStatus: null,
  paymentMethod: null,
  provider: [],
  fromDate: null,
  toDate: null,
  minAmount: null,
  maxAmount: null,
  hasRefund: null,
  excludeLegacy: null,
  page: 1,
  size: 20,
  sort: 'CREATED_AT_DESC',
};

export const usePaymentFiltersStore = create<PaymentFiltersState>()(
  devtools(
    (set) => ({
      ...INITIAL,
      setKeyword: (keyword) => set({ keyword, page: 1 }, false, 'setKeyword'),
      setPaymentStatus: (paymentStatus) =>
        set({ paymentStatus, page: 1 }, false, 'setPaymentStatus'),
      setPaymentMethod: (paymentMethod) =>
        set({ paymentMethod, page: 1 }, false, 'setPaymentMethod'),
      setProvider: (provider) => set({ provider, page: 1 }, false, 'setProvider'),
      setFromDate: (fromDate) => set({ fromDate, page: 1 }, false, 'setFromDate'),
      setToDate: (toDate) => set({ toDate, page: 1 }, false, 'setToDate'),
      setMinAmount: (minAmount) => set({ minAmount, page: 1 }, false, 'setMinAmount'),
      setMaxAmount: (maxAmount) => set({ maxAmount, page: 1 }, false, 'setMaxAmount'),
      setHasRefund: (hasRefund) => set({ hasRefund, page: 1 }, false, 'setHasRefund'),
      setExcludeLegacy: (excludeLegacy) => set({ excludeLegacy, page: 1 }, false, 'setExcludeLegacy'),
      setPage: (page) => set({ page }, false, 'setPage'),
      setSize: (size) => set({ size, page: 1 }, false, 'setSize'),
      setSort: (sort) => set({ sort, page: 1 }, false, 'setSort'),
      reset: () => set({ ...INITIAL }, false, 'reset'),
    }),
    { name: 'payment-filters' },
  ),
);
